import { useState, useEffect, useRef } from 'react'
import { apiClient } from '../services/api'
import { apiClient as webApiClient } from '../services/api'
import printerService from '../services/printerService'
import toast from 'react-hot-toast'
import { useKeyboardShortcuts, KeyboardShortcutsHelp } from '../hooks/useKeyboardShortcuts'
import { useNavigationGuard } from '../hooks/useNavigationGuard'
import { cartPersistenceService, CartState, SuspendedSale, CompletedSale } from '../services/cartPersistence'
import { useAuthStore } from '../stores/authStore'
import { formatCurrency } from '../lib/utils'
import { POSHeader } from '../components/pos/POSHeader'
import { POSTopHeader } from '../components/pos/POSTopHeader'
import { CustomerSelection } from '../components/pos/CustomerSelection'
import { ProductSearch } from '../components/pos/ProductSearch'
import { CartTable } from '../components/pos/CartTable'
import { CheckoutSummary } from '../components/pos/CheckoutSummary'
import { AddCustomerModal } from '../components/pos/AddCustomerModal'
import { ProductGrid } from '../components/pos/ProductGrid'
import { SidebarCart } from '../components/pos/SidebarCart'
import { CategorySidenav } from '../components/pos/CategorySidenav'
import { BillPreviewModal } from '../components/pos/BillPreviewModal'
import { SuspendedSalesModal } from '../components/pos/SuspendedSalesModal'
import { SuspendSaleModal } from '../components/pos/SuspendSaleModal'
import { SaleDetailsModal } from '../components/pos/SaleDetailsModal'
import { CartRestorationModal } from '../components/pos/CartRestorationModal'
import { QuickAddProduct } from '../components/pos/QuickAddProduct'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { Button } from '../components/ui/Button'
import { MobilePOSLayout } from '../components/pos/MobilePOSLayout'
import { Trash2, ShoppingCart, Plus } from 'lucide-react'

interface Product {
  id: string
  name: string
  sku: string
  price: number | string
  stock_quantity: number
  category_name?: string
  gst_rate?: number
  gst_type?: 'inclusive' | 'exclusive'
  unit?: string
  unit_allow_decimal?: number | boolean // 1 or true = allows decimals, 0 or false = whole numbers only
}

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  gst_number?: string
}

interface CartItem {
  id: string
  name: string
  sku: string
  originalPrice: number
  price: number // Can be overridden
  quantity: number
  total: number
  gst_rate: number // GST rate for this item
  gst_type: 'inclusive' | 'exclusive' // Whether GST is included in price or added on top
  discount?: {
    type: 'fixed' | 'percentage'
    value: number
    amount: number
  }
  isWeighable?: boolean // For decimal quantities
  unit?: string // kg, grams, pieces, etc
}

interface CustomerForm {
  name: string
  phone: string
  email: string
  gst_number: string
  address: string
}

export const POSSystemNew = () => {
  const { user } = useAuthStore()
  
  // Layout and UI State
  const [posLayout, setPosLayout] = useState<'cards' | 'table'>('cards')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [categorySidenavCollapsed, setCategorySidenavCollapsed] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  // Data State
  const [products, setProducts] = useState<Product[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)

  // Pagination State
  const [productsPagination, setProductsPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  })
  const [loadingMore, setLoadingMore] = useState(false)

  // Search State
  const [searchTerm, setSearchTerm] = useState('')
  const [searching, setSearching] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Payment State
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'upi' | 'split'>('cash')
  const [amountReceived, setAmountReceived] = useState('')
  const [splitPayments, setSplitPayments] = useState({
    cash: '',
    card: '',
    upi: ''
  })

  // Discount and Tax State
  const [orderDiscount, setOrderDiscount] = useState<{
    type: 'fixed' | 'percentage'
    value: number
  } | null>(null)
  const [taxRate, setTaxRate] = useState(18) // Default GST rate
  const [showItemDiscountModal, setShowItemDiscountModal] = useState<string | null>(null)
  const [showPriceOverrideModal, setShowPriceOverrideModal] = useState<string | null>(null)

  // Modal State
  const [showAddCustomer, setShowAddCustomer] = useState(false)
  const [showBillPreview, setShowBillPreview] = useState(false)
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false)
  const [showCartRestoration, setShowCartRestoration] = useState(false)
  const [showQuickAddProduct, setShowQuickAddProduct] = useState(false)
  const [savedCartData, setSavedCartData] = useState<CartState | null>(null)
  
  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
    variant?: 'danger' | 'warning' | 'info'
    confirmText?: string
    icon?: React.ReactNode
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'warning'
  })
  
  // Sale Completion State
  const [saleCompletionData, setSaleCompletionData] = useState<{
    isVisible: boolean
    billNumber: string
    customer?: Customer | null
    totalAmount: number
    change: number
  } | null>(null)

  // Suspended Sales State
  const [suspendedSales, setSuspendedSales] = useState<SuspendedSale[]>([])
  const [showSuspendedSales, setShowSuspendedSales] = useState(false)
  const [showSuspendSale, setShowSuspendSale] = useState(false)
  
  // Recent Orders State
  const [recentOrders, setRecentOrders] = useState<any[]>([])
  const [selectedSaleForDetails, setSelectedSaleForDetails] = useState<any | null>(null)
  const [showSaleDetails, setShowSaleDetails] = useState(false)
  
  // Auto-save reference
  const autoSaveRef = useRef<boolean>(false)

  // Form State
  const [customerForm, setCustomerForm] = useState<CustomerForm>({
    name: '',
    phone: '',
    email: '',
    gst_number: '',
    address: ''
  })
  const [highlightedItems, setHighlightedItems] = useState<Set<string>>(new Set())

  // Load initial data and restore cart
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const [productsResponse, customersResponse, categoriesResponse] = await Promise.all([
          apiClient.getProducts({ limit: 50, offset: 0 }),
          apiClient.getCustomers(),
          apiClient.getCategories()
        ])
        const productsData = productsResponse.products || productsResponse.data?.products || []
        const pagination = productsResponse.pagination || { total: productsData.length, limit: 50, offset: 0 }

        setProducts(productsData)
        setProductsPagination({
          total: pagination.total,
          limit: pagination.limit,
          offset: pagination.offset,
          hasMore: (pagination.offset + productsData.length) < pagination.total
        })
        setCustomers(customersResponse.customers || customersResponse.data?.customers || [])
        setCategories(categoriesResponse.categories || categoriesResponse.data?.categories || [])

        // Load suspended sales and recent orders
        setSuspendedSales(cartPersistenceService.getSuspendedSales())
        await loadRecentOrders()

        // Check for saved cart and show restoration modal if exists
        const savedCart = cartPersistenceService.loadActiveCart()
        console.log('Checking for saved cart:', savedCart)
        if (savedCart && savedCart.cart.length > 0) {
          console.log('Found saved cart with', savedCart.cart.length, 'items')
          setSavedCartData(savedCart)
          setShowCartRestoration(true)
        } else {
          console.log('No saved cart found')
        }
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to load data'
        toast.error(errorMessage)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  // Auto-save cart state
  useEffect(() => {
    if (cart.length > 0 && !autoSaveRef.current) {
      autoSaveRef.current = true
      cartPersistenceService.startAutoSave(() => getCurrentCartState())
      console.log('Started auto-save for cart')
    } else if (cart.length === 0 && autoSaveRef.current) {
      autoSaveRef.current = false
      cartPersistenceService.stopAutoSave()
      cartPersistenceService.clearActiveCart()
      console.log('Stopped auto-save and cleared cart')
    }

    return () => {
      if (autoSaveRef.current) {
        cartPersistenceService.stopAutoSave()
      }
    }
  }, [cart.length])

  // Save cart whenever cart state changes (not just length)
  useEffect(() => {
    if (cart.length > 0) {
      const timeoutId = setTimeout(() => {
        cartPersistenceService.saveActiveCart(getCurrentCartState())
        console.log('Manual cart save triggered')
      }, 500) // Debounce saves

      return () => clearTimeout(timeoutId)
    }
  }, [cart, selectedCustomer, paymentMethod, amountReceived, splitPayments, orderDiscount])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cartPersistenceService.stopAutoSave()
    }
  }, [])

  // Listen for fullscreen change events (ESC key, browser controls, etc.)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange) // Safari
    document.addEventListener('mozfullscreenchange', handleFullscreenChange) // Firefox
    document.addEventListener('MSFullscreenChange', handleFullscreenChange) // IE/Edge

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [])

  // Listen for F11 key to toggle fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault() // Prevent browser's default F11 behavior
        toggleFullscreen()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Handle category selection - fetch products for selected category
  useEffect(() => {
    const fetchProductsByCategory = async () => {
      if (!selectedCategory && products.length > 0) return // Already loaded initial products

      try {
        setLoading(true)
        const categoryId = categories.find(c => c.name === selectedCategory)?.id

        const productsResponse = await apiClient.getProducts({
          limit: 50,
          offset: 0,
          category_id: categoryId || undefined,
          search: searchTerm || undefined
        })

        const productsData = productsResponse.products || productsResponse.data?.products || []
        const pagination = productsResponse.pagination || { total: productsData.length, limit: 50, offset: 0 }

        setProducts(productsData)
        setProductsPagination({
          total: pagination.total,
          limit: pagination.limit,
          offset: pagination.offset,
          hasMore: (pagination.offset + productsData.length) < pagination.total
        })
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to load products'
        toast.error(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    if (selectedCategory !== null) {
      fetchProductsByCategory()
    }
  }, [selectedCategory])

  // Handle search with debouncing
  const handleSearch = (search: string) => {
    setSearchTerm(search)

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSearching(true)
        const categoryId = categories.find(c => c.name === selectedCategory)?.id

        const productsResponse = await apiClient.getProducts({
          limit: 50,
          offset: 0,
          search: search || undefined,
          category_id: categoryId || undefined
        })

        const productsData = productsResponse.products || productsResponse.data?.products || []
        const pagination = productsResponse.pagination || { total: productsData.length, limit: 50, offset: 0 }

        setProducts(productsData)
        setProductsPagination({
          total: pagination.total,
          limit: pagination.limit,
          offset: pagination.offset,
          hasMore: (pagination.offset + productsData.length) < pagination.total
        })
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to search products'
        toast.error(errorMessage)
      } finally {
        setSearching(false)
      }
    }, 300)
  }

  // Load more products
  const loadMoreProducts = async () => {
    if (loadingMore || !productsPagination.hasMore) return

    try {
      setLoadingMore(true)
      const newOffset = productsPagination.offset + productsPagination.limit
      const categoryId = categories.find(c => c.name === selectedCategory)?.id

      const productsResponse = await apiClient.getProducts({
        limit: 50,
        offset: newOffset,
        search: searchTerm || undefined,
        category_id: categoryId || undefined
      })

      const newProducts = productsResponse.products || productsResponse.data?.products || []
      const pagination = productsResponse.pagination || { total: 0, limit: 50, offset: newOffset }

      setProducts(prev => [...prev, ...newProducts])
      setProductsPagination({
        total: pagination.total,
        limit: pagination.limit,
        offset: newOffset,
        hasMore: (newOffset + newProducts.length) < pagination.total
      })

      toast.success(`Loaded ${newProducts.length} more products`)
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load more products'
      toast.error(errorMessage)
    } finally {
      setLoadingMore(false)
    }
  }

  // Import formatCurrency from utils - removed local override

  // Load recent orders from web API with fallback to local storage
  const loadRecentOrders = async () => {
    try {
      // Use web API directly to get real database data
      const response = await webApiClient.getSales({
        limit: 20,
        sort: 'created_at DESC'
      })
      const salesData = response.sales || response.data?.sales || []
      console.log('Loaded recent orders:', salesData.length, 'sales')
      setRecentOrders(salesData)
    } catch (error) {
      console.error('Failed to load recent orders from web API:', error)
      // Fallback to local storage if web API is not available
      try {
        const localSales = cartPersistenceService.getRecentSales()
        // Convert local storage format to API format
        const formattedSales = localSales.map(sale => ({
          id: sale.id,
          bill_number: sale.billNumber,
          invoice_number: sale.billNumber,
          created_at: new Date(sale.completedAt).toISOString(),
          total_amount: sale.totalAmount,
          payment_method: sale.paymentMethod,
          customer: sale.selectedCustomer,
          items: sale.cart.map(item => ({
            product_name: item.name,
            quantity: item.quantity,
            unit_price: item.price,
            total_amount: item.total
          }))
        }))
        setRecentOrders(formattedSales)
      } catch (localError) {
        console.error('Failed to load from local storage:', localError)
        setRecentOrders([])
      }
    }
  }

  // Get current cart state for persistence
  const getCurrentCartState = (): CartState => ({
    cart,
    selectedCustomer,
    paymentMethod,
    amountReceived,
    splitPayments,
    orderDiscount,
    taxRate,
    timestamp: Date.now(),
    id: `cart_${Date.now()}`
  })

  // Advanced calculation functions
  const calculateItemTotal = (item: CartItem) => {
    let itemTotal = item.price * item.quantity
    
    // Apply item-level discount
    if (item.discount) {
      if (item.discount.type === 'percentage') {
        itemTotal -= (itemTotal * item.discount.value) / 100
      } else {
        itemTotal -= item.discount.value
      }
    }
    
    return Math.max(0, itemTotal)
  }

  // Calculate true subtotal (base amount without tax)
  const cartSubtotal = cart.reduce((sum, item) => {
    if (item.gst_type === 'inclusive') {
      // For inclusive GST, extract the base amount from the price (before discounts)
      const gstRate = typeof item.gst_rate === 'number' ? item.gst_rate : 0
      const basePrice = (item.price * 100) / (100 + gstRate)
      let baseAmount = basePrice * item.quantity
      
      // Apply item-level discount to base amount
      if (item.discount) {
        if (item.discount.type === 'percentage') {
          baseAmount -= (baseAmount * item.discount.value) / 100
        } else {
          // For fixed discount, we need to extract the base portion from the discount
          const discountBaseAmount = (item.discount.value * 100) / (100 + gstRate)
          baseAmount -= discountBaseAmount
        }
      }
      
      return sum + Math.max(0, baseAmount)
    } else {
      // For exclusive GST, use the calculateItemTotal (which is the base amount)
      return sum + calculateItemTotal(item)
    }
  }, 0)
  
  // Apply order-level discount
  let cartTotal = cartSubtotal
  let orderDiscountAmount = 0
  if (orderDiscount) {
    if (orderDiscount.type === 'percentage') {
      orderDiscountAmount = (cartSubtotal * orderDiscount.value) / 100
    } else {
      orderDiscountAmount = orderDiscount.value
    }
    cartTotal = Math.max(0, cartSubtotal - orderDiscountAmount)
  }
  
  // Calculate tax per item based on individual GST rates and type (inclusive/exclusive)
  const taxAmount = cart.reduce((totalTax, item) => {
    if (!item || typeof item.total !== 'number') {
      return totalTax
    }
    
    const itemSubtotal = item.total - (item.discount?.amount || 0)
    const gstRate = typeof item.gst_rate === 'number' ? item.gst_rate : 0 // Default to 0% if undefined
    const gstType = item.gst_type || 'inclusive' // Default to inclusive if not specified
    
    let itemTax = 0
    if (gstType === 'inclusive') {
      // For inclusive GST: tax = (amount * rate) / (100 + rate)
      itemTax = (itemSubtotal * gstRate) / (100 + gstRate)
    } else {
      // For exclusive GST: tax = (amount * rate) / 100
      itemTax = (itemSubtotal * gstRate) / 100
    }
    
    return totalTax + (isFinite(itemTax) ? itemTax : 0)
  }, 0)
  
  // Apply order discount to tax as well (proportionally)
  const adjustedTaxAmount = orderDiscount && cartSubtotal > 0
    ? taxAmount * (cartTotal / cartSubtotal) // Reduce tax proportionally to order discount
    : taxAmount
  
  const safeTaxAmount = isFinite(adjustedTaxAmount) ? adjustedTaxAmount : 0
  
  // Calculate final total = subtotal + all taxes
  // This works for both inclusive and exclusive GST because:
  // - safeTaxAmount includes tax from inclusive items (extracted portion)
  // - safeTaxAmount includes tax from exclusive items (added portion)
  const finalTotal = cartTotal + safeTaxAmount
  
  const splitTotal = parseFloat(splitPayments.cash || '0') + 
                     parseFloat(splitPayments.card || '0') + 
                     parseFloat(splitPayments.upi || '0')

  // Helper function to round to specified decimal places
  const roundToDecimal = (value: number, decimals: number = 2): number => {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
  }

  // Cart functions
  const addToCart = (product: Product) => {
    const price = parseFloat(product.price?.toString() || '0')
    const existingItem = cart.find(item => item.id === product.id)

    // Add highlighting for this item
    setHighlightedItems(prev => new Set(prev).add(product.id))

    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(product.id)
        return newSet
      })
    }, 2000)

    if (existingItem) {
      const increment = existingItem.isWeighable ? 0.1 : 1
      // Round the new quantity to avoid floating point precision issues
      const newQuantity = roundToDecimal(existingItem.quantity + increment, 2)
      updateQuantity(product.id, newQuantity)
    } else {
      // Determine if product allows decimal quantities based on unit configuration from database
      // Check if unit_allow_decimal is set (can be 1, true, or '1')
      const isWeighable = product.unit_allow_decimal === 1 ||
                         product.unit_allow_decimal === true ||
                         product.unit_allow_decimal === '1' ||
                         // Fallback to unit name check if allow_decimal is not set
                         (product.unit_allow_decimal === undefined && (
                           product.category_name?.toLowerCase().includes('weight') ||
                           product.category_name?.toLowerCase().includes('bulk')
                         ))

      const initialQty = isWeighable ? 0.1 : 1
      const newItem: CartItem = {
        id: product.id,
        name: product.name,
        sku: product.sku,
        originalPrice: price,
        price,
        quantity: roundToDecimal(initialQty, 2),
        total: roundToDecimal(price * initialQty, 2),
        gst_rate: typeof product.gst_rate === 'number' ? product.gst_rate : 0, // Use product's GST rate or 0% if undefined
        gst_type: product.gst_type || 'inclusive', // Default to inclusive if not specified
        isWeighable,
        unit: product.unit || (isWeighable ? 'kg' : 'piece')
      }
      setCart(prev => [...prev, newItem])
    }
    toast.success(`${product.name} added to cart`)
  }

  const handleQuickAddProduct = async (productData: any) => {
    try {
      // Create the product via API
      const response = await webApiClient.createProduct(productData)
      const newProduct = response.product || response.data?.product || response

      // Add to products list
      setProducts(prev => [...prev, newProduct])

      // Add to cart immediately
      addToCart(newProduct)

      toast.success('Product added to inventory and cart!')
    } catch (error) {
      console.error('Failed to create product:', error)
      throw error // Let the modal handle the error
    }
  }

  const updateQuantity = (productId: string, quantity: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === productId) {
        const minQty = item.isWeighable ? 0.01 : 1
        // Round to 2 decimal places to avoid floating point precision issues
        const validQuantity = roundToDecimal(Math.max(minQty, quantity), 2)
        return {
          ...item,
          quantity: validQuantity,
          total: roundToDecimal(calculateItemTotal({...item, quantity: validQuantity}), 2)
        }
      }
      return item
    }))
  }

  // New functions for advanced features
  const applyItemDiscount = (itemId: string, discountType: 'fixed' | 'percentage', value: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const discount = {
          type: discountType,
          value,
          amount: discountType === 'percentage' 
            ? (item.price * item.quantity * value) / 100
            : value
        }
        const updatedItem = { ...item, discount }
        return { ...updatedItem, total: calculateItemTotal(updatedItem) }
      }
      return item
    }))
    toast.success(`Discount applied to item`)
  }

  const removeItemDiscount = (itemId: string) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, discount: undefined }
        return { ...updatedItem, total: calculateItemTotal(updatedItem) }
      }
      return item
    }))
    toast.success(`Discount removed from item`)
  }

  const overrideItemPrice = (itemId: string, newPrice: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, price: newPrice }
        return { ...updatedItem, total: calculateItemTotal(updatedItem) }
      }
      return item
    }))
    toast.success(`Price updated`)
  }

  const applyOrderDiscount = (discountType: 'fixed' | 'percentage', value: number) => {
    setOrderDiscount({ type: discountType, value })
    toast.success(`Order discount applied: ${discountType === 'percentage' ? `${value}%` : `₹${value}`}`)
  }

  const removeOrderDiscount = () => {
    setOrderDiscount(null)
    toast.success(`Order discount removed`)
  }

  const removeFromCart = (productId: string) => {
    const item = cart.find(item => item.id === productId)
    const itemName = item?.name || 'item'
    
    setConfirmModal({
      isOpen: true,
      title: 'Remove Item',
      message: `Are you sure you want to remove "${itemName}" from the cart?`,
      variant: 'danger',
      confirmText: 'Remove',
      icon: <Trash2 className="h-6 w-6" />,
      onConfirm: () => {
        setCart(prev => prev.filter(item => item.id !== productId))
        toast.success('Item removed from cart')
      }
    })
  }

  const clearCart = () => {
    const itemCount = cart.length
    const itemText = itemCount === 1 ? 'item' : 'items'
    
    setConfirmModal({
      isOpen: true,
      title: 'Clear Cart',
      message: `Are you sure you want to clear the entire cart? This will remove all ${itemCount} ${itemText}.`,
      variant: 'warning',
      confirmText: 'Clear Cart',
      icon: <ShoppingCart className="h-6 w-6" />,
      onConfirm: () => {
        setCart([])
        toast.success('Cart cleared')
      }
    })
  }

  // Customer functions
  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId)
    setSelectedCustomer(customer || null)
  }

  const addCustomer = async () => {
    if (!customerForm.name.trim()) {
      toast.error('Customer name is required')
      return
    }

    try {
      const result = await apiClient.createCustomer(customerForm)
      toast.success('Customer added successfully')
      
      // Reload customers
      const response = await apiClient.getCustomers()
      const customersData = response.customers || response.data?.customers || []
      setCustomers(customersData)

      // Auto-select the newly created customer
      // Handle both API response structures: result.customer or just result
      const newCustomer = result.customer || result
      if (newCustomer && newCustomer.id) {
        const createdCustomer = customersData.find(c => c.id === newCustomer.id)
        if (createdCustomer) {
          setSelectedCustomer(createdCustomer)
        }
      } else {
        // Fallback: Try to find by name if ID matching fails
        const createdCustomer = customersData.find(c =>
          c.name === customerForm.name && c.phone === customerForm.phone
        )
        if (createdCustomer) {
          setSelectedCustomer(createdCustomer)
        }
      }
      
      // Reset form and close modal
      setCustomerForm({ name: '', phone: '', email: '', gst_number: '', address: '' })
      setShowAddCustomer(false)
    } catch (err: any) {
      // Display the actual error message from the backend
      const errorMessage = err?.message || 'Failed to add customer'
      toast.error(errorMessage)
    }
  }

  // Payment functions
  const processSale = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty')
      return
    }

    try {
      const saleData = {
        customer_id: selectedCustomer?.id || null,
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          sku: item.sku,
          quantity: item.quantity,
          price: item.price,
          total: item.total
        })),
        subtotal: cartTotal,
        tax_amount: safeTaxAmount,
        total_amount: finalTotal,
        payment_method: paymentMethod,
        amount_received: paymentMethod === 'cash' ? parseFloat(amountReceived || '0') : finalTotal,
        payment_details: paymentMethod === 'split' ? splitPayments : null
      }

      const saleResponse = await apiClient.createSale(saleData)

      // Calculate change for cash payments
      const change = paymentMethod === 'cash'
        ? Math.max(0, parseFloat(amountReceived || '0') - finalTotal)
        : 0

      const billNumber = saleResponse.bill?.bill_number || `POS-${Date.now()}`

      // Refresh recent orders and products from API after successful sale
      try {
        await loadRecentOrders()
      } catch (error) {
        console.error('Failed to refresh recent orders:', error)
        // Don't block the sale completion if this fails
      }

      // Refresh products to get updated stock quantities
      // Note: Only refreshing currently loaded products to maintain pagination
      try {
        const currentLimit = productsPagination.offset + productsPagination.limit
        const productsResponse = await apiClient.getProducts({ limit: currentLimit, offset: 0 })
        setProducts(productsResponse.products || productsResponse.data?.products || [])
        console.log('Product stock refreshed after sale')
      } catch (error) {
        console.error('Failed to refresh products:', error)
        // Don't block the sale completion if this fails
      }

      // Show sale completion overlay immediately
      setSaleCompletionData({
        isVisible: true,
        billNumber,
        customer: selectedCustomer,
        totalAmount: finalTotal,
        change
      })

    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to process sale'
      toast.error(errorMessage)
    }
  }

  // Handle print receipt
  const handlePrintReceipt = async () => {
    if (!saleCompletionData) return

    try {
      const printData = {
        type: 'receipt' as const,
        billNumber: saleCompletionData.billNumber,
        date: new Date().toLocaleDateString('en-IN'),
        customerName: selectedCustomer?.name,
        customerPhone: selectedCustomer?.phone,
        items: cart.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.total
        })),
        subtotal: cartSubtotal,
        taxAmount: safeTaxAmount,
        totalAmount: finalTotal,
        paymentMethod: paymentMethod,
        amountReceived: paymentMethod === 'cash' ? parseFloat(amountReceived || '0') : finalTotal,
        change: saleCompletionData.change
      }

      await printerService.printDocument(printData)
      toast.success('Receipt printed successfully!')
    } catch (printError: any) {
      console.error('Print failed:', printError)
      const errorMessage = printError?.message || 'Failed to print receipt'
      toast.error(errorMessage)
    }
  }

  // Handle skip print
  const handleSkipPrint = () => {
    // Just proceed to new sale - no action needed
  }

  // Handle new sale
  const handleNewSale = () => {
    // Reset everything
    setCart([])
    setAmountReceived('')
    setSplitPayments({ cash: '', card: '', upi: '' })
    setSelectedCustomer(null)
    setPaymentMethod('cash')
    setOrderDiscount(null)
    setSaleCompletionData(null)
    
    toast.success('Ready for new sale!')
  }

  // Cart restoration functions
  const handleRestoreCart = () => {
    if (!savedCartData) return
    
    setCart(savedCartData.cart)
    setSelectedCustomer(savedCartData.selectedCustomer)
    setPaymentMethod(savedCartData.paymentMethod || 'cash')
    setAmountReceived(savedCartData.amountReceived || '')
    setSplitPayments(savedCartData.splitPayments || { cash: '', card: '', upi: '' })
    setOrderDiscount(savedCartData.orderDiscount || null)
    setTaxRate(savedCartData.taxRate || 18)
    
    setShowCartRestoration(false)
    setSavedCartData(null)
    toast.success('Previous cart restored successfully!')
  }

  const handleSuspendAndStartNew = async () => {
    if (!savedCartData) return
    
    try {
      // Suspend the current saved cart
      const suspendedId = cartPersistenceService.suspendSale(
        savedCartData,
        'Auto-suspended to start new sale',
        user?.full_name
      )

      // Clear the active cart
      cartPersistenceService.clearActiveCart()
      
      // Refresh suspended sales list
      setSuspendedSales(cartPersistenceService.getSuspendedSales())
      
      setShowCartRestoration(false)
      setSavedCartData(null)
      toast.success('Previous cart saved to suspended sales. Starting new sale!')
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to suspend cart'
      toast.error(errorMessage)
    }
  }

  const handleDiscardAndStartNew = () => {
    if (!savedCartData) return
    
    // Clear the saved cart permanently
    cartPersistenceService.clearActiveCart()
    
    setShowCartRestoration(false)
    setSavedCartData(null)
    toast.success('Previous cart discarded. Starting new sale!')
  }

  // Suspended sales functions
  const handleSuspendSale = async (note?: string) => {
    if (cart.length === 0) {
      toast.error('Cart is empty')
      return
    }

    try {
      const suspendedId = cartPersistenceService.suspendSale(
        getCurrentCartState(),
        note,
        user?.full_name
      )

      // Clear current cart
      setCart([])
      setAmountReceived('')
      setSplitPayments({ cash: '', card: '', upi: '' })
      setSelectedCustomer(null)
      setPaymentMethod('cash')
      setOrderDiscount(null)

      // Refresh suspended sales list
      setSuspendedSales(cartPersistenceService.getSuspendedSales())
      
      toast.success('Sale suspended successfully!')
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to suspend sale'
      toast.error(errorMessage)
    }
  }

  const handleRecallSale = (saleId: string) => {
    if (cart.length > 0) {
      const shouldReplace = confirm(
        'You have items in your current cart. Replace with suspended sale?'
      )
      if (!shouldReplace) return
    }

    const recalledState = cartPersistenceService.recallSuspendedSale(saleId)
    if (!recalledState) {
      toast.error('Failed to recall sale')
      return
    }

    // Restore the recalled state
    setCart(recalledState.cart)
    setSelectedCustomer(recalledState.selectedCustomer)
    setPaymentMethod(recalledState.paymentMethod)
    setAmountReceived(recalledState.amountReceived)
    setSplitPayments(recalledState.splitPayments)
    setOrderDiscount(recalledState.orderDiscount)
    setTaxRate(recalledState.taxRate)

    // Refresh suspended sales list
    setSuspendedSales(cartPersistenceService.getSuspendedSales())
    setShowSuspendedSales(false)
    
    toast.success('Sale recalled successfully!')
  }

  const handleDeleteSuspendedSale = (saleId: string) => {
    if (cartPersistenceService.deleteSuspendedSale(saleId)) {
      setSuspendedSales(cartPersistenceService.getSuspendedSales())
      toast.success('Suspended sale deleted')
    } else {
      toast.error('Failed to delete suspended sale')
    }
  }

  // Recent Orders functions
  const handleReprintReceipt = async (saleId: string) => {
    try {
      // Try web API first
      const response = await webApiClient.getSale(saleId)
      const sale = response.sale || response.data?.sale

      if (!sale) {
        toast.error('Sale not found')
        return
      }

      const printData = {
        type: 'receipt' as const,
        billNumber: sale.bill?.bill_number || sale.bill_number || sale.invoice_number || sale.id,
        date: new Date(sale.created_at).toLocaleDateString('en-IN'),
        customerName: sale.customer?.name,
        customerPhone: sale.customer?.phone,
        items: (sale.items || []).map((item: any) => ({
          name: item.product_name || item.name,
          quantity: parseFloat(item.quantity || 0),
          price: parseFloat(item.unit_price || item.price || 0),
          total: parseFloat(item.total_price || item.total_amount || item.total || 0)
        })),
        subtotal: parseFloat(sale.subtotal || 0),
        taxAmount: parseFloat(sale.tax_amount || 0),
        totalAmount: parseFloat(sale.total_amount || 0),
        paymentMethod: sale.payment_method,
        amountReceived: parseFloat(sale.amount_received || 0),
        change: parseFloat(sale.change || 0)
      }

      await printerService.printDocument(printData)
      toast.success('Receipt reprinted successfully!')
    } catch (error) {
      console.error('Reprint failed from API:', error)
      // Fallback to local storage
      try {
        const localSale = cartPersistenceService.getRecentSale(saleId)
        if (!localSale) {
          toast.error('Sale not found')
          return
        }

        const printData = {
          type: 'receipt' as const,
          billNumber: localSale.billNumber,
          date: new Date(localSale.completedAt).toLocaleDateString('en-IN'),
          customerName: localSale.selectedCustomer?.name,
          customerPhone: localSale.selectedCustomer?.phone,
          items: localSale.cart.map(item => ({
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            total: item.total
          })),
          subtotal: localSale.subtotal,
          taxAmount: localSale.taxAmount,
          totalAmount: localSale.totalAmount,
          paymentMethod: localSale.paymentMethod,
          amountReceived: localSale.amountReceived,
          change: localSale.change
        }

        await printerService.printDocument(printData)
        toast.success('Receipt reprinted successfully!')
      } catch (localError: any) {
        console.error('Local reprint failed:', localError)
        const errorMessage = localError?.message || 'Failed to reprint receipt'
        toast.error(errorMessage)
      }
    }
  }

  const handleViewOrder = async (saleId: string) => {
    try {
      // Try web API first
      const response = await webApiClient.getSale(saleId)
      const sale = response.sale || response.data?.sale

      if (!sale) {
        toast.error('Sale not found')
        return
      }

      setSelectedSaleForDetails(sale)
      setShowSaleDetails(true)
    } catch (error) {
      console.error('Failed to load sale details from API:', error)
      // Fallback to local storage
      try {
        const localSale = cartPersistenceService.getRecentSale(saleId)
        if (!localSale) {
          toast.error('Sale not found')
          return
        }

        // Convert local storage format to API format
        const formattedSale = {
          id: localSale.id,
          bill_number: localSale.billNumber,
          invoice_number: localSale.billNumber,
          created_at: new Date(localSale.completedAt).toISOString(),
          created_by: localSale.completedBy,
          customer: localSale.selectedCustomer,
          items: localSale.cart.map(item => ({
            product_name: item.name,
            sku: item.sku,
            quantity: item.quantity,
            unit_price: item.price,
            total_amount: item.total,
            discount_amount: item.discount?.amount || 0
          })),
          subtotal: localSale.subtotal,
          tax_amount: localSale.taxAmount,
          discount_amount: localSale.orderDiscount ? 
            (localSale.orderDiscount.type === 'percentage' ? 
              (localSale.subtotal * localSale.orderDiscount.value / 100) : 
              localSale.orderDiscount.value) : 0,
          total_amount: localSale.totalAmount,
          payment_method: localSale.paymentMethod,
          amount_received: localSale.amountReceived,
          change: localSale.change,
          payment_details: localSale.splitPayments
        }

        setSelectedSaleForDetails(formattedSale)
        setShowSaleDetails(true)
      } catch (localError: any) {
        console.error('Failed to load from local storage:', localError)
        const errorMessage = localError?.message || 'Failed to load sale details'
        toast.error(errorMessage)
      }
    }
  }

  // Fullscreen toggle - Browser native fullscreen (F11 equivalent)
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        await document.documentElement.requestFullscreen()
        setIsFullscreen(true)
      } else {
        // Exit fullscreen
        await document.exitFullscreen()
        setIsFullscreen(false)
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error)
      toast.error('Fullscreen toggle failed')
    }
  }

  // Keyboard shortcuts helper functions
  const handleToggleLayout = () => {
    setPosLayout(prev => prev === 'cards' ? 'table' : 'cards')
  }

  const handleCloseModals = () => {
    setShowAddCustomer(false)
    setShowBillPreview(false)
    setShowItemDiscountModal(null)
    setShowPriceOverrideModal(null)
    // Don't auto-close sale completion overlay - user must choose an action
  }

  const canCompleteSale = 
    (paymentMethod === 'cash' && amountReceived && parseFloat(amountReceived) >= finalTotal) ||
    (paymentMethod === 'card' || paymentMethod === 'upi') ||
    (paymentMethod === 'split' && splitTotal >= finalTotal)

  const isAnyModalOpen = showAddCustomer || showBillPreview || showItemDiscountModal !== null || showPriceOverrideModal !== null || saleCompletionData?.isVisible || showSuspendedSales || showSuspendSale

  // Navigation protection
  useNavigationGuard({
    hasActiveCart: cart.length > 0,
    onNavigationAttempt: async (targetPath) => {
      if (cart.length === 0) return true
      
      const shouldSuspend = confirm(
        `You have ${cart.length} items in your cart. Would you like to suspend this sale before leaving?`
      )
      
      if (shouldSuspend) {
        await handleSuspendSale(`Auto-suspended before navigation to ${targetPath}`)
        return true
      }
      
      // Save the cart before allowing navigation
      const shouldSave = confirm('Your cart will be automatically saved and restored when you return to POS.')
      if (shouldSave) {
        cartPersistenceService.saveActiveCart(getCurrentCartState())
        console.log('Cart saved before navigation to:', targetPath)
        return true
      }
      
      return false
    }
  })

  // Initialize keyboard shortcuts
  useKeyboardShortcuts({
    onAddCustomer: () => setShowAddCustomer(true),
    onPreviewBill: () => setShowBillPreview(true),
    onCompleteSale: processSale,
    onClearCart: clearCart,
    onToggleLayout: handleToggleLayout,
    onToggleFullscreen: toggleFullscreen,
    onCloseModals: handleCloseModals,
    isModalOpen: isAnyModalOpen,
    canCompleteSale: canCompleteSale && cart.length > 0,
    hasItems: cart.length > 0
  })

  // Products are already filtered by backend (search/category)
  // When category is selected, we fetch from API in useEffect below
  const filteredProducts = products

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Mobile Layout - Dedicated mobile POS experience */}
      <div className="lg:hidden">
        <MobilePOSLayout
          cart={cart}
          onUpdateQuantity={updateQuantity}
          onRemoveFromCart={removeFromCart}
          onClearCart={clearCart}
          highlightedItems={highlightedItems}
          products={filteredProducts}
          onAddToCart={addToCart}
          onQuickAdd={() => setShowQuickAddProduct(true)}
          customers={customers}
          selectedCustomer={selectedCustomer}
          onCustomerChange={handleCustomerChange}
          onAddCustomer={() => setShowAddCustomer(true)}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          amountReceived={amountReceived}
          setAmountReceived={setAmountReceived}
          cartSubtotal={cartSubtotal}
          taxAmount={safeTaxAmount}
          finalTotal={finalTotal}
          formatCurrency={formatCurrency}
          onProcessSale={processSale}
          canCompleteSale={canCompleteSale}
          onPreviewBill={() => setShowBillPreview(true)}
          onApplyItemDiscount={applyItemDiscount}
          onRemoveItemDiscount={removeItemDiscount}
          onOverridePrice={overrideItemPrice}
          suspendedSalesCount={suspendedSales.length}
          onSuspendSale={() => setShowSuspendSale(true)}
          onShowSuspendedSales={() => setShowSuspendedSales(true)}
          recentOrders={recentOrders}
          onReprintReceipt={handleReprintReceipt}
          onViewOrder={handleViewOrder}
          onSettings={undefined}
          onReports={undefined}
          onUserManagement={undefined}
          saleCompletionData={saleCompletionData}
          onPrintReceipt={handlePrintReceipt}
          onSkipPrint={handleSkipPrint}
          onNewSale={handleNewSale}
        />
      </div>

      {/* Desktop Layout - Original desktop POS experience */}
      <div className="hidden lg:flex flex-1 flex-col h-full">
        {/* Desktop Header */}
        <POSTopHeader
          posLayout={posLayout}
          setPosLayout={setPosLayout}
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
          hasActiveCart={cart.length > 0}
          suspendedSalesCount={suspendedSales.length}
          onSuspendSale={() => setShowSuspendSale(true)}
          onShowSuspendedSales={() => setShowSuspendedSales(true)}
          recentOrders={recentOrders}
          onReprintReceipt={handleReprintReceipt}
          onViewOrder={handleViewOrder}
          formatCurrency={formatCurrency}
        />

        {/* Desktop Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {posLayout === 'cards' ? (
            // Cards Layout
            <>
              {/* Category Sidenav */}
              <CategorySidenav
                products={products}
                onCategorySelect={setSelectedCategory}
                selectedCategory={selectedCategory}
                isCollapsed={categorySidenavCollapsed}
                onToggle={() => setCategorySidenavCollapsed(!categorySidenavCollapsed)}
              />

              {/* Main Content */}
              <div className="flex-1 flex overflow-hidden">
                {/* Product Grid */}
                <div className="flex-1 flex flex-col p-6 overflow-hidden">
                  <div className="flex-1 min-h-0">
                    <ProductGrid
                      products={filteredProducts}
                      onAddToCart={addToCart}
                      formatCurrency={formatCurrency}
                      onQuickAdd={() => setShowQuickAddProduct(true)}
                      hasMore={productsPagination.hasMore}
                      loadingMore={loadingMore}
                      onLoadMore={loadMoreProducts}
                      totalProducts={productsPagination.total}
                      onSearch={handleSearch}
                      searching={searching}
                    />
                  </div>
                </div>

                {/* Sidebar Cart */}
                <div className="w-96 flex-shrink-0 flex flex-col overflow-hidden">
                  <SidebarCart
                    cart={cart}
                    customers={customers}
                    selectedCustomer={selectedCustomer}
                    onUpdateQuantity={updateQuantity}
                    onRemoveFromCart={removeFromCart}
                    onClearCart={clearCart}
                    onCustomerChange={handleCustomerChange}
                    onAddCustomer={() => setShowAddCustomer(true)}
                    formatCurrency={formatCurrency}
                    cartTotal={cartTotal}
                    highlightedItems={highlightedItems}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    amountReceived={amountReceived}
                    setAmountReceived={setAmountReceived}
                    splitPayments={splitPayments}
                    setSplitPayments={setSplitPayments}
                    splitTotal={splitTotal}
                    onPreviewBill={() => setShowBillPreview(true)}
                    onProcessSale={processSale}
                    cartSubtotal={cartSubtotal}
                    orderDiscount={orderDiscount}
                    orderDiscountAmount={orderDiscountAmount}
                    taxRate={taxRate}
                    taxAmount={safeTaxAmount}
                    finalTotal={finalTotal}
                    onApplyItemDiscount={applyItemDiscount}
                    onRemoveItemDiscount={removeItemDiscount}
                    onOverridePrice={overrideItemPrice}
                    onApplyOrderDiscount={applyOrderDiscount}
                    onRemoveOrderDiscount={removeOrderDiscount}
                    saleCompletionData={saleCompletionData}
                    onPrintReceipt={handlePrintReceipt}
                    onSkipPrint={handleSkipPrint}
                    onNewSale={handleNewSale}
                  />
                </div>
              </div>
            </>
          ) : (
            // Table Layout
            <div className="flex-1 flex gap-6 p-6">
              {/* Left Side - Search + Cart Table */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Search Section */}
                <div className="mb-6">
                  <ProductSearch
                    products={filteredProducts}
                    onAddToCart={addToCart}
                    formatCurrency={formatCurrency}
                    onQuickAdd={() => setShowQuickAddProduct(true)}
                  />
                </div>

                {/* Cart Table */}
                <div className="flex-1 overflow-hidden">
                  <CartTable
                    cart={cart}
                    onUpdateQuantity={updateQuantity}
                    onRemoveFromCart={removeFromCart}
                    onClearCart={clearCart}
                    formatCurrency={formatCurrency}
                    highlightedItems={highlightedItems}
                    onApplyItemDiscount={applyItemDiscount}
                    onRemoveItemDiscount={removeItemDiscount}
                    onOverridePrice={overrideItemPrice}
                  />
                </div>
              </div>
                
              {/* Right Side - Checkout Summary */}
              {cart.length > 0 && (
                <div className="w-96 flex-shrink-0 flex flex-col overflow-hidden">
                  <SidebarCart
                    cart={cart}
                    customers={customers}
                    selectedCustomer={selectedCustomer}
                    onUpdateQuantity={updateQuantity}
                    onRemoveFromCart={removeFromCart}
                    onClearCart={clearCart}
                    onCustomerChange={handleCustomerChange}
                    onAddCustomer={() => setShowAddCustomer(true)}
                    formatCurrency={formatCurrency}
                    cartTotal={cartTotal}
                    highlightedItems={highlightedItems}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    amountReceived={amountReceived}
                    setAmountReceived={setAmountReceived}
                    splitPayments={splitPayments}
                    setSplitPayments={setSplitPayments}
                    splitTotal={splitTotal}
                    onPreviewBill={() => setShowBillPreview(true)}
                    onProcessSale={processSale}
                    cartSubtotal={cartSubtotal}
                    orderDiscount={orderDiscount}
                    orderDiscountAmount={orderDiscountAmount}
                    taxRate={taxRate}
                    taxAmount={safeTaxAmount}
                    finalTotal={finalTotal}
                    onApplyItemDiscount={applyItemDiscount}
                    onRemoveItemDiscount={removeItemDiscount}
                    onOverridePrice={overrideItemPrice}
                    onApplyOrderDiscount={applyOrderDiscount}
                    onRemoveOrderDiscount={removeOrderDiscount}
                    saleCompletionData={saleCompletionData}
                    onPrintReceipt={handlePrintReceipt}
                    onSkipPrint={handleSkipPrint}
                    onNewSale={handleNewSale}
                    hideCartItems={true}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={showAddCustomer}
        onClose={() => {
          setShowAddCustomer(false)
          setCustomerForm({ name: '', phone: '', email: '', gst_number: '', address: '' })
        }}
        customerForm={customerForm}
        setCustomerForm={setCustomerForm}
        onSubmit={addCustomer}
      />

      {/* Quick Add Product Modal */}
      <QuickAddProduct
        isOpen={showQuickAddProduct}
        onClose={() => setShowQuickAddProduct(false)}
        categories={categories}
        onProductCreated={handleQuickAddProduct}
        formatCurrency={formatCurrency}
      />

      {/* Bill Preview Modal */}
      <BillPreviewModal
        isOpen={showBillPreview}
        onClose={() => setShowBillPreview(false)}
        cart={cart}
        customer={selectedCustomer}
        cartSubtotal={cartSubtotal}
        orderDiscount={orderDiscount}
        orderDiscountAmount={orderDiscountAmount}
        taxRate={taxRate}
        taxAmount={safeTaxAmount}
        finalTotal={finalTotal}
        paymentMethod={paymentMethod}
        formatCurrency={formatCurrency}
      />

      {/* Suspended Sales Modal */}
      <SuspendedSalesModal
        isOpen={showSuspendedSales}
        onClose={() => setShowSuspendedSales(false)}
        suspendedSales={suspendedSales}
        onRecallSale={handleRecallSale}
        onDeleteSale={handleDeleteSuspendedSale}
        formatCurrency={formatCurrency}
      />

      {/* Suspend Sale Modal */}
      <SuspendSaleModal
        isOpen={showSuspendSale}
        onClose={() => setShowSuspendSale(false)}
        onSuspend={handleSuspendSale}
        cartItemCount={cart.length}
        cartTotal={finalTotal}
        formatCurrency={formatCurrency}
      />

      {/* Cart Restoration Modal */}
      <CartRestorationModal
        isOpen={showCartRestoration}
        onClose={() => setShowCartRestoration(false)}
        savedCartData={savedCartData}
        onRestore={handleRestoreCart}
        onSuspendAndNew={handleSuspendAndStartNew}
        onDiscardAndNew={handleDiscardAndStartNew}
        formatCurrency={formatCurrency}
      />

      {/* Sale Details Modal */}
      <SaleDetailsModal
        isOpen={showSaleDetails}
        onClose={() => setShowSaleDetails(false)}
        sale={selectedSaleForDetails}
        formatCurrency={formatCurrency}
        onReprint={handleReprintReceipt}
      />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp isVisible={showKeyboardHelp} />
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
        icon={confirmModal.icon}
      />
      
      {/* Help Toggle Button */}
      <button
        onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
        className="fixed bottom-4 left-4 bg-orange-500 hover:bg-orange-600 text-white p-2 rounded-full shadow-lg z-40 transition-colors"
        title="Toggle Keyboard Shortcuts Help"
      >
        <span className="text-xs font-bold">?</span>
      </button>
    </div>
  )
}